import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import HRHeader from "../components/HRHeader";
import { useSearchParams } from "react-router-dom";
import api, { downloadEsgReadinessPdf } from "../api";
import { toast } from "sonner";
import {
  Sprout, CheckCircle2, AlertTriangle, XCircle, ExternalLink, RefreshCw,
  Database, CalendarClock, Info, ChevronDown, ChevronRight, SlidersHorizontal, FileDown,
  Send, ShieldCheck, Trash2, RadioTower,
} from "lucide-react";

const AVAIL = {
  AVAILABLE: { label: "Disponibile", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  PARTIAL: { label: "Parziale", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  NOT_AVAILABLE: { label: "Non disponibile", cls: "bg-red-100 text-red-700 border-red-200" },
};
const barColor = (p) => (p >= 90 ? "#009640" : p >= 50 ? "#f59e0b" : "#c00510");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};
const vsmeText = (arr) => (arr || []).filter((v) => v && v !== "-").map((v) => "VSME " + v).join(" · ") || "Contesto";

function AvailBadge({ status }) {
  const a = AVAIL[status] || { label: status, cls: "bg-gray-100 text-gray-600 border-gray-200" };
  let Icon = Info;
  if (status === "AVAILABLE" || status === "COMPLETE") Icon = CheckCircle2;
  else if (status === "PARTIAL" || status === "ESTIMATED") Icon = AlertTriangle;
  else if (status === "NOT_AVAILABLE") Icon = XCircle;
  return <span className={"inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border " + a.cls}><Icon size={11} /> {a.label}</span>;
}

const flattenTree = (obj) => {
  const rows = [];
  const walk = (node, depth, prefix) => {
    if (node === null || typeof node !== "object") return;
    Object.entries(node).forEach(([k, v]) => {
      if (k === "methodology" || k === "note") return;
      const id = prefix + "." + k;
      const isObj = v && typeof v === "object";
      rows.push({
        id,
        depth,
        label: k.replace(/_/g, " "),
        value: isObj ? null : (v === null || v === undefined ? "—" : String(v)),
        isObj,
      });
      if (isObj) walk(v, depth + 1, id);
    });
  };
  if (obj && typeof obj === "object") walk(obj, 0, "root");
  return rows;
};

const DataTree = ({ obj }) => {
  if (obj === null || obj === undefined) return <span className="text-gray-400">—</span>;
  if (typeof obj !== "object") return <span className="text-[#16345c] font-semibold">{String(obj)}</span>;
  const rows = flattenTree(obj);
  return (
    <div>
      {rows.map((r) => (
        <div
          key={r.id}
          className={"flex items-start gap-2 py-0.5 text-[12px] " + (r.depth ? "border-l border-gray-100" : "")}
          style={{ paddingLeft: r.depth ? r.depth * 12 : 0 }}
        >
          <span className="text-gray-500 min-w-[130px] capitalize">{r.label}</span>
          {r.isObj
            ? <span className="text-gray-300 text-[11px]">›</span>
            : <span className="text-[#16345c] font-semibold">{r.value}</span>}
        </div>
      ))}
    </div>
  );
};

const Sparkline = ({ points, threshold }) => {
  if (!points || points.length < 2) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6" data-testid="esg-trend">
        <div className="text-[12px] font-bold uppercase tracking-wide text-gray-400 mb-1">Andamento completezza</div>
        <p className="text-[13px] text-gray-500">Raccolta dati in corso: il grafico dell'andamento si popolerà nei prossimi giorni (viene registrato un punto al giorno).</p>
      </div>
    );
  }
  const W = 600, H = 90, pad = 6;
  const n = points.length;
  const vals = points.map((p) => p.overall_completeness || 0);
  const x = (i) => pad + (i * (W - 2 * pad)) / (n - 1);
  const y = (v) => H - pad - (v / 100) * (H - 2 * pad);
  const line = vals.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${pad},${H - pad} ${line} ${(W - pad)},${H - pad}`;
  const last = vals[n - 1], first = vals[0];
  const delta = Math.round((last - first) * 10) / 10;
  const thrY = y(threshold);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6" data-testid="esg-trend">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] font-bold uppercase tracking-wide text-gray-400">Andamento completezza · {n} rilevazioni</div>
        <div className={"text-[12px] font-bold " + (delta >= 0 ? "text-emerald-600" : "text-[#c00510]")}>{delta >= 0 ? "+" : "−"}{Math.abs(delta)}% dal primo dato</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 90 }} preserveAspectRatio="none">
        <polygon points={area} fill="#009640" opacity="0.08" />
        <line x1={pad} x2={W - pad} y1={thrY} y2={thrY} stroke="#c00510" strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />
        <polyline points={line} fill="none" stroke="#009640" strokeWidth="2" />
        <circle cx={x(n - 1)} cy={y(last)} r="3.5" fill="#009640" />
      </svg>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1">
        <span>{points[0].date}</span>
        <span className="text-[#c00510]">soglia {threshold}%</span>
        <span>{points[n - 1].date}</span>
      </div>
    </div>
  );
};

const ReadinessTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thr, setThr] = useState(80);
  const [editingThr, setEditingThr] = useState(false);
  const [savingThr, setSavingThr] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/esg/readiness").then((r) => { setData(r.data); if (typeof r.data.threshold === "number") setThr(r.data.threshold); })
      .catch(() => toast.error("Errore nel caricamento")).finally(() => setLoading(false));
    api.get("/esg/readiness/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveThreshold = async () => {
    setSavingThr(true);
    try {
      await api.put("/esg/settings", { completeness_threshold: Number(thr) });
      toast.success("Soglia aggiornata");
      setEditingThr(false); setDismissed(false); load();
    } catch { toast.error("Errore nel salvataggio della soglia"); }
    finally { setSavingThr(false); }
  };

  if (loading && !data) return <div className="p-10 text-center text-gray-400 text-[14px]">Caricamento…</div>;
  if (!data) return null;
  const overall = data.overall_completeness || 0;
  const below = data.below_threshold && !dismissed;

  return (
    <div data-testid="esg-readiness-tab">
      {below && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="esg-completeness-alert">
          <AlertTriangle size={20} className="text-[#c00510] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-[14px] font-bold text-[#c00510]">Completezza dati ESG sotto la soglia</div>
            <div className="text-[13px] text-gray-700 mt-0.5">La completezza core è al <b>{overall}%</b>, sotto la soglia impostata del <b>{data.threshold}%</b>. Completa i domini "Parziale"/"Non disponibile" qui sotto per raggiungere l'obiettivo.</div>
          </div>
          <button onClick={() => setDismissed(true)} data-testid="esg-alert-dismiss" className="text-gray-400 hover:text-gray-600 text-[12px] font-bold shrink-0">Nascondi</button>
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6" data-testid="esg-overall">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[12px] font-bold uppercase tracking-wide text-gray-400">Completezza dati ESG (core)</div>
            <div className="flex items-end gap-3 mt-1">
              <div className="text-[44px] leading-none font-black" style={{ color: barColor(overall) }} data-testid="esg-overall-pct">{overall}%</div>
              <div className="text-[13px] text-gray-500 mb-1">{data.headcount} dipendenti · dati al {String(data.as_of || "").slice(0, 10)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editingThr ? (
              <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5">
                <span className="text-[12px] text-gray-500">Soglia avviso</span>
                <input type="number" min="0" max="100" value={thr} data-testid="esg-threshold-input" onChange={(e) => setThr(e.target.value)} className="w-16 border border-gray-300 rounded-sm px-2 py-1 text-[13px] outline-none focus:border-[#16345c]" />
                <span className="text-[12px] text-gray-500">%</span>
                <button onClick={saveThreshold} disabled={savingThr} data-testid="esg-threshold-save" className="text-[12px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] px-2.5 py-1 rounded-sm disabled:opacity-60">{savingThr ? "…" : "Salva"}</button>
                <button onClick={() => { setEditingThr(false); setThr(data.threshold); }} className="text-[12px] text-gray-500 px-1">Annulla</button>
              </div>
            ) : (
              <button onClick={() => setEditingThr(true)} data-testid="esg-threshold-edit" className="flex items-center gap-1.5 text-gray-600 border border-gray-300 hover:bg-gray-50 text-[12px] font-semibold px-3 py-2 rounded-sm transition-colors"><SlidersHorizontal size={14} /> Soglia {data.threshold}%</button>
            )}
            <button onClick={load} data-testid="esg-refresh" className="flex items-center gap-2 text-[#16345c] border border-gray-300 hover:bg-gray-50 text-[13px] font-semibold px-3 py-2 rounded-sm transition-colors"><RefreshCw size={15} /> Aggiorna</button>
          </div>
        </div>
        <div className="mt-4 h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: overall + "%", backgroundColor: barColor(overall) }} />
        </div>
        <p className="text-[12px] text-gray-500 mt-3 flex items-start gap-1.5"><Info size={14} className="mt-0.5 shrink-0" /> La percentuale considera i domini "core" compilabili in JOBTIMEHR (dipendenti, contratti, FTE, genere, ore, formazione, CCNL, management). Completa i dati mancanti per portare tutti i domini a "Disponibile".</p>
      </div>

      <Sparkline points={history?.points} threshold={history?.threshold ?? data.threshold} />

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="esg-domains">
        {(data.domains || []).map((d) => {
          const hasComp = typeof d.completeness === "number";
          return (
            <div key={d.domain} data-testid={"esg-domain-" + d.domain} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="text-[14px] font-bold text-[#16345c]">{d.label}</div>
                  <div className="text-[11px] text-gray-400">{vsmeText(d.vsme)}</div>
                </div>
                <AvailBadge status={d.availability} />
              </div>

              {hasComp ? (
                <div className="mb-2">
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <span className="text-gray-500">{d.filled != null && d.total != null ? d.filled + "/" + d.total + " compilati" : "Completezza"}</span>
                    <span className="font-bold" style={{ color: barColor(d.completeness) }}>{d.completeness}%</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: d.completeness + "%", backgroundColor: barColor(d.completeness) }} />
                  </div>
                </div>
              ) : (
                <div className="text-[12px] text-gray-500 mb-2">{d.filled != null ? d.filled + " elementi registrati" : "Dato contestuale / derivato"}</div>
              )}

              {d.missing ? (
                <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5 mb-2" data-testid={"esg-missing-" + d.domain}>{d.missing}</div>
              ) : (
                <div className="text-[12px] text-emerald-700 mb-2">Dato completo ✓</div>
              )}

              <div className="mt-auto flex items-center justify-between pt-1 gap-2">
                <span className="text-[10.5px] text-gray-400 leading-tight">Fonte: {d.source}<br />Agg.: {fmtDate(d.last_updated)}</span>
                {d.missing && d.deep_link ? (
                  <Link to={d.deep_link} data-testid={"esg-complete-" + d.domain} className="flex items-center gap-1 text-[12px] font-bold text-[#0d6cb8] hover:text-[#0b5c9e] shrink-0"><ExternalLink size={13} /> Completa</Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PreviewTab = () => {
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [exporting, setExporting] = useState(false);

  const load = useCallback((y) => {
    setLoading(true);
    api.get("/esg/preview?year=" + y).then((r) => setData(r.data)).catch(() => toast.error("Errore anteprima")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(year); }, [load, year]);

  const exportPdf = async () => {
    setExporting(true);
    try { await downloadEsgReadinessPdf(year, `esg_readiness_${data?.company || "azienda"}_${year}.pdf`); toast.success("Report ESG scaricato"); }
    catch { toast.error("Errore nel download del report"); }
    finally { setExporting(false); }
  };

  const years = [nowY, nowY - 1, nowY - 2, nowY - 3];

  return (
    <div data-testid="esg-preview-tab">
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-[13px] font-bold text-[#16345c]">Anno di rendicontazione</span>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="esg-preview-year" className="border border-gray-300 rounded-sm px-3 h-9 text-[13px] text-[#16345c] outline-none focus:border-[#16345c]">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {data && data.frozen ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-sky-100 text-sky-700 border-sky-200">Anno congelato</span> : null}
        </div>
        <button onClick={exportPdf} disabled={exporting} data-testid="esg-export-pdf" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 h-9 rounded-sm transition-colors disabled:opacity-60"><FileDown size={15} /> {exporting ? "Generazione…" : "Esporta PDF"}</button>
      </div>

      {loading && !data ? <div className="p-10 text-center text-gray-400 text-[14px]">Caricamento…</div> : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="esg-preview-datasets">
          {data && data.datasets && Object.keys(data.datasets).map((ep) => {
            const ds = data.datasets[ep];
            const isOpen = !!open[ep];
            return (
              <div key={ep} data-testid={"esg-preview-" + ep} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="text-[14px] font-bold text-[#16345c]">{ds.label}</div>
                    <div className="text-[11px] text-gray-400">{vsmeText(ds.vsme)} · <code className="text-gray-400">{ep}</code></div>
                  </div>
                  <AvailBadge status={ds.status} />
                </div>
                <div className="border-t border-gray-100 pt-2">
                  <DataTree obj={ds.data} />
                </div>
                {(ds.warnings || []).length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {ds.warnings.map((w, i) => <div key={i} className="text-[11px] text-amber-700 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}</div>)}
                  </div>
                ) : null}
                <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[10.5px] text-gray-400">
                  <span><Database size={11} className="inline mr-1" />Fonte: {ds.source}</span>
                  <span><CalendarClock size={11} className="inline mr-1" />{fmtDate(ds.last_modified)}</span>
                </div>
                {ds.data && ds.data.methodology ? (
                  <button onClick={() => setOpen(Object.assign({}, open, { [ep]: !isOpen }))} className="mt-2 text-[11px] text-[#0d6cb8] font-semibold flex items-center gap-1">
                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Metodologia
                  </button>
                ) : null}
                {isOpen && ds.data && ds.data.methodology ? (
                  <p className="text-[11px] text-gray-500 mt-1">{typeof ds.data.methodology === "object" ? JSON.stringify(ds.data.methodology) : ds.data.methodology}</p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SYNC_STATUS = {
  success: { label: "Inviato", cls: "bg-emerald-100 text-emerald-700" },
  mocked: { label: "Simulato", cls: "bg-amber-100 text-amber-700" },
  skipped: { label: "Nessun dato", cls: "bg-gray-100 text-gray-600" },
  error: { label: "Errore", cls: "bg-red-100 text-red-700" },
};

const SyncTab = () => {
  const nowY = new Date().getFullYear();
  const [config, setConfig] = useState(null);
  const [validated, setValidated] = useState([]);
  const [logs, setLogs] = useState([]);
  const [endpoint, setEndpoint] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [auto, setAuto] = useState(false);
  const [valYear, setValYear] = useState(nowY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/esg/sync/config").then((r) => { setConfig(r.data); setEndpoint(r.data.endpoint || ""); setAuto(!!r.data.auto); }).catch(() => {});
    api.get("/esg/validated").then((r) => setValidated(r.data.years || [])).catch(() => {});
    api.get("/esg/sync/logs").then((r) => setLogs(r.data.logs || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setBusy(true);
    try {
      const body = { endpoint, auto };
      if (apiKey) body.api_key = apiKey;
      await api.put("/esg/sync/config", body);
      setApiKey(""); toast.success("Configurazione salvata"); load();
    } catch { toast.error("Errore nel salvataggio"); } finally { setBusy(false); }
  };

  const validateYear = async () => {
    setBusy(true);
    try { await api.post(`/esg/validate/${valYear}`); toast.success(`Anno ${valYear} validato`); load(); }
    catch { toast.error("Errore nella validazione"); } finally { setBusy(false); }
  };

  const removeYear = async (y) => {
    try { await api.delete(`/esg/validate/${y}`); toast.success(`Validazione ${y} rimossa`); load(); }
    catch { toast.error("Errore"); }
  };

  const runSync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/esg/sync/run");
      const st = r.data.status;
      if (st === "mocked") toast.info("Invio simulato: configura l'endpoint ESG-PMI per l'invio reale.");
      else if (st === "success") toast.success("Sincronizzazione completata");
      else if (st === "skipped") toast.warning("Nessun anno validato da sincronizzare");
      else toast.error(r.data.message || "Errore di sincronizzazione");
      load();
    } catch { toast.error("Errore di sincronizzazione"); } finally { setBusy(false); }
  };

  const years = [nowY, nowY - 1, nowY - 2, nowY - 3];

  return (
    <div data-testid="esg-sync-tab" className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="esg-sync-config">
        <div className="flex items-center gap-2 mb-1"><RadioTower size={18} className="text-[#0d6cb8]" /><h3 className="text-[15px] font-black text-[#16345c]">Connessione ESG-PMI</h3>
          {config && (config.configured
            ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Configurato</span>
            : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Non configurato — invio simulato</span>)}
        </div>
        <p className="text-[13px] text-gray-500 mb-4">Endpoint di ingest e chiave API del sistema ESG-PMI. Finché non sono impostati, la sincronizzazione è <b>simulata</b> (nessuna chiamata esterna).</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">Endpoint ESG-PMI</label>
            <input value={endpoint} onChange={(e) => setEndpoint(e.target.value)} data-testid="esg-sync-endpoint" placeholder="https://esg-pmi.example.com/api/ingest" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">API key {config?.has_api_key && <span className="text-gray-400 font-normal">(salvata: {config.api_key_masked})</span>}</label>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="esg-sync-apikey" placeholder={config?.has_api_key ? "•••••• (lascia vuoto per non cambiare)" : "Chiave API"} className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-[13px] mt-4 cursor-pointer w-fit">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="esg-sync-auto" className="accent-[#16345c] w-4 h-4" />
          Sincronizzazione automatica giornaliera degli anni validati
        </label>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={saveConfig} disabled={busy} data-testid="esg-sync-save" className="bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm disabled:opacity-60">Salva configurazione</button>
          {config?.last_sync && <span className="text-[12px] text-gray-400">Ultima sincronizzazione: {fmtDate(config.last_sync)}</span>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="esg-validated">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck size={18} className="text-[#009640]" /><h3 className="text-[15px] font-black text-[#16345c]">Anni validati (as-of 31/12)</h3></div>
        <p className="text-[13px] text-gray-500 mb-4">Valida la fotografia ESG di fine anno: gli aggregati vengono congelati e resi disponibili per la sincronizzazione.</p>
        <div className="flex items-center gap-2 mb-4">
          <select value={valYear} onChange={(e) => setValYear(Number(e.target.value))} data-testid="esg-sync-validate-year" className="border border-gray-300 rounded-sm px-3 h-9 text-[13px] outline-none focus:border-[#16345c]">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={validateYear} disabled={busy} data-testid="esg-sync-validate-btn" className="flex items-center gap-2 bg-[#009640] hover:bg-[#007a33] text-white text-[13px] font-bold px-4 h-9 rounded-sm disabled:opacity-60"><ShieldCheck size={15} /> Valida anno</button>
        </div>
        {validated.length === 0 ? <p className="text-[13px] text-gray-400">Nessun anno validato.</p> : (
          <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
            {validated.map((v) => (
              <div key={v.year} data-testid={`esg-validated-${v.year}`} className="flex items-center justify-between px-3 py-2 text-[13px]">
                <span className="font-bold text-[#16345c]">{v.year}</span>
                <span className="text-gray-500 text-[12px]">completezza {v.overall_completeness}% · validato {fmtDate(v.validated_at)}{v.synced_at ? ` · sincronizzato ${fmtDate(v.synced_at)}` : ""}</span>
                <button onClick={() => removeYear(v.year)} data-testid={`esg-validated-del-${v.year}`} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
        <button onClick={runSync} disabled={busy} data-testid="esg-sync-run" className="mt-4 flex items-center gap-2 bg-[#0d6cb8] hover:bg-[#0a5590] text-white text-[13px] font-bold px-4 py-2 rounded-sm disabled:opacity-60"><Send size={15} /> Sincronizza ora</button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="esg-sync-logs">
        <h3 className="text-[15px] font-black text-[#16345c] mb-3">Log sincronizzazioni</h3>
        {logs.length === 0 ? <p className="text-[13px] text-gray-400">Nessuna sincronizzazione effettuata.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead><tr className="text-[11px] uppercase text-gray-400 text-left border-b border-gray-200"><th className="py-2 pr-2">Data</th><th className="py-2 pr-2">Esito</th><th className="py-2 pr-2">Anni</th><th className="py-2">Messaggio</th></tr></thead>
              <tbody>
                {logs.map((l) => {
                  const s = SYNC_STATUS[l.status] || { label: l.status, cls: "bg-gray-100 text-gray-600" };
                  return (
                    <tr key={l.id} className="border-b border-gray-50 text-[13px]">
                      <td className="py-2 pr-2 text-gray-600 whitespace-nowrap">{fmtDate(l.ts)}</td>
                      <td className="py-2 pr-2"><span className={"text-[11px] font-bold px-2 py-0.5 rounded-full " + s.cls}>{s.label}</span></td>
                      <td className="py-2 pr-2 text-gray-600">{(l.years || []).join(", ") || "—"}</td>
                      <td className="py-2 text-gray-500">{l.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const EsgSustainabilityPage = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return ["readiness", "preview", "sync"].includes(t) ? t : "readiness";
  });
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && ["readiness", "preview", "sync"].includes(t)) setTab(t);
  }, [searchParams]);
  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Sprout size={24} className="text-[#009640]" /> ESG / Sostenibilità</h1>
          <p className="text-[14px] text-gray-500">Disponibilità e completezza dei dati Social che JOBTIMEHR mette a disposizione di ESG-PMI. Compila i dati mancanti direttamente nelle sezioni operative.</p>
        </div>

        <div className="flex items-center gap-2 mb-6 border-b border-gray-200 flex-wrap">
          {[["readiness", "Disponibilità & Completezza"], ["preview", "Anteprima dati ESG-PMI"], ["sync", "Sincronizzazione ESG-PMI"]].map((t) => (
            <button key={t[0]} onClick={() => setTab(t[0])} data-testid={"esg-tab-" + t[0]}
              className={"px-4 py-2.5 text-[13px] font-bold border-b-2 -mb-px transition-colors " + (tab === t[0] ? "border-[#ff7e00] text-[#16345c]" : "border-transparent text-gray-400 hover:text-gray-600")}>
              {t[1]}
            </button>
          ))}
        </div>

        {tab === "readiness" ? <ReadinessTab /> : tab === "preview" ? <PreviewTab /> : <SyncTab />}
      </main>
    </div>
  );
};

export default EsgSustainabilityPage;
